import { API_BASE } from "./config.js";
import { user, admin } from "./auth.js";
import { accounts } from "./accounts.js";
import { network, isNetworkError } from "./cache/net.js";

// Pick the CSRF token that matches the route's auth context. Admin routes are
// gated by the mc_admin session; user routes by mc_user_N. Sending whichever
// happens to be loaded (e.g. an admin token to a user route) is rejected as
// an invalid CSRF token - so choose by path.
function csrfFor(path) {
  if (path.startsWith("/v1/admin/")) return admin.csrfToken() ?? null;
  return user.csrfToken() ?? admin.csrfToken() ?? null;
}

// Re-fetch the session so a stale CSRF token is replaced with the current one.
async function refreshCsrf(path) {
  try {
    if (path.startsWith("/v1/admin/")) {
      const me = await request("GET", "/v1/admin/me");
      if (me) admin.set(me);
    } else {
      const me = await request("GET", "/v1/me");
      if (me) user.set(me);
    }
    return true;
  } catch { return false; }
}

// Hard ceiling on how long a single API call may hang. `navigator.onLine` is
// unreliable (it stays true on a dropped Wi-Fi or an unreachable server), and a
// bare fetch then waits on the OS TCP timeout - 60-120s of spinner that reads as
// "still loading online" when we're actually offline. We abort well before that
// and flip to the offline state so the cached UI + banner take over. Kept above
// the backend's own 20s IMAP bound so we never cut off a legitimately slow sync.
const REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS   = 120_000; // uploads stream large files - be generous

// fetch() with an AbortController timeout. Always clears the timer so a fast
// response doesn't leave a pending abort armed.
async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function request(method, path, body, _retried = false) {
  const headers = { "Content-Type": "application/json" };

  // Tell the backend which signed-in account this request belongs to. Admin
  // routes ignore the header (they use mc_admin), and unauthenticated calls
  // (e.g. /v1/auth/login, /v1/accounts) tolerate slot=0 silently. Sending it
  // unconditionally keeps the api.js call site clean.
  if (!path.startsWith("/v1/admin/")) {
    headers["X-Account-Slot"] = String(accounts.activeSlot());
  }

  // Send CSRF token only on state-changing requests.
  // Read from in-memory session - cookies are cross-origin and unreachable
  // via document.cookie when the API is on a different subdomain.
  const needsCsrf = !["GET", "HEAD"].includes(method);
  if (needsCsrf) {
    const csrf = csrfFor(path);
    if (csrf) headers["X-Csrf-Token"] = csrf;
  }

  let res;
  try {
    res = await fetchWithTimeout(API_BASE + path, {
      method,
      credentials: "include",
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }, REQUEST_TIMEOUT_MS);
  } catch (err) {
    // A timeout (AbortError) or a TypeError both mean we couldn't reach the
    // server - treat either as "offline" so the cached UI takes over instead
    // of the spinner hanging until the OS gives up.
    if (err?.name === "AbortError" || isNetworkError(err)) network.markOffline();
    throw err;
  }
  // Got a response - the network is reachable, even if the server returned
  // an error code. Promote our state out of "offline" if it was stuck there.
  network.markOnline();

  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    const loc = window.location.pathname;
    const loginPath =
      loc.startsWith("/control-center") || loc.startsWith("/admin")
        ? "/admin/login"
        : "/login";

    // DROP THE DEAD SESSION FIRST. Without this the persisted session stays in
    // memory + localStorage, so LoginPage's "already signed in" guard fires,
    // navigates straight back to "/", the inbox 401s again, and we bounce
    // between "/" and "/login". The router's 350ms loop guard then swallows the
    // render and the user is left staring at a BLANK PAGE. Clearing it makes
    // the login form actually render.
    // Dynamic import keeps api.js free of a static auth.js dependency.
    // Only redirect if we're not already on the login page.
    // Either way, always throw so the caller's catch block runs -
    // returning undefined here made the login form treat a failed
    // login as success and navigate to the inbox.
    if (loc !== loginPath) {
      // Scoped to the redirect branch ON PURPOSE: a 401 raised while the user
      // is ALREADY on the login page is a failed sign-in attempt (including
      // "Add another account", where a good session is still live) - clearing
      // there would sign them out for mistyping a password.
      try {
        const { user, admin } = await import("./auth.js");
        if (loginPath === "/admin/login") admin.clear(); else user.clear();
      } catch { /* never let cleanup block the redirect */ }

      window.history.pushState({}, "", loginPath);
      const { render } = await import("./router.js");
      render();
    }

    throw new Error(data.message ?? "Invalid credentials");
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({ message: res.statusText }));

    // Self-heal a stale/mismatched CSRF token: refresh it from the session
    // and retry the request once. Covers the case where storage was wiped or
    // the session was re-established and the in-memory token went stale.
    if (
      res.status === 403 && needsCsrf && !_retried &&
      /csrf/i.test(data.message ?? "") && data.error !== "PASSWORD_CHANGE_REQUIRED"
    ) {
      if (await refreshCsrf(path)) {
        return request(method, path, body, true);
      }
    }

    if (res.status === 403 && data.error === "PASSWORD_CHANGE_REQUIRED") {
      window.history.pushState({}, "", "/force-password-change");
      const { render } = await import("./router.js");
      render();
    }

    const err = new Error(data.message ?? "Request failed");
    err.status = res.status;
    throw err;
  }

  return res.status === 204 ? null : res.json();
}

// Multipart upload helper. Sends a single File (or Blob) under the field
// name "file" so the backend's request.file() picks it up. Returns parsed
// JSON. Honours the same CSRF + X-Account-Slot headers as request().
async function upload(path, file, fieldName = "file") {
  const headers = {};
  const csrf = csrfFor(path);
  if (csrf) headers["X-Csrf-Token"] = csrf;
  if (!path.startsWith("/v1/admin/")) {
    headers["X-Account-Slot"] = String(accounts.activeSlot());
  }
  const form = new FormData();
  form.append(fieldName, file, file.name);
  let res;
  try {
    res = await fetchWithTimeout(API_BASE + path, {
      method: "POST",
      credentials: "include",
      headers,
      body: form,
    }, UPLOAD_TIMEOUT_MS);
  } catch (err) {
    if (err?.name === "AbortError" || isNetworkError(err)) network.markOffline();
    throw err;
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(data.message ?? "Upload failed");
  }
  return res.status === 204 ? null : res.json();
}

export const api = {
  get:    (path)             => request("GET",    path),
  // Binary download (mailbox archives). Bypasses request()'s JSON handling but
  // keeps `credentials: "include"` so the session cookie rides along. No
  // timeout: an archive can be hundreds of MB.
  getBlob: async (path) => {
    const res = await fetch(API_BASE + path, { method: "GET", credentials: "include" });
    if (!res.ok) {
      const msg = await res.json().then((d) => d.message).catch(() => res.statusText);
      throw new Error(msg || `download failed (${res.status})`);
    }
    return res.blob();
  },
  post:   (path, body)       => request("POST",   path, body),
  put:    (path, body)       => request("PUT",    path, body),
  patch:  (path, body)       => request("PATCH",  path, body),
  delete: (path, body)       => request("DELETE", path, body),
  upload: (path, file)       => upload(path, file),
};
